(function (root) {
  'use strict';

  const shortId = value => String(value).split('/').pop();
  const signature = cart => JSON.stringify(cart.items.map(item => [item.key, item.quantity, item.original_line_price]));

  const completeCart = cart => Boolean(cart && Array.isArray(cart.items) && typeof cart.token === 'string' && typeof cart.currency === 'string' && Number.isFinite(cart.items_subtotal_price));
  const cartVersion = cart => JSON.stringify([cart.token, cart.currency, signature(cart)]);

  class WarrantySync {
    constructor(adapter) {
      this.adapter = adapter;
      this.pending = null;
      this.dirty = false;
      this.ready = false;
      this.error = null;
      this.revision = 0;
      this.nextCart = null;
      this.currentCart = null;
      this.version = null;
    }
    request(cart, invalidate = false) {
      if (completeCart(cart)) {
        const version = cartVersion(cart);
        if (!invalidate && version === this.version) {
          if (this.pending) return this.pending;
          if (this.ready) return Promise.resolve(this.currentCart);
        }
        this.version = version;
        this.nextCart = cart;
      } else {
        if (this.pending && !invalidate) return this.pending;
        this.nextCart = null;
        this.version = null;
      }
      this.revision++;
      this.dirty = true;
      this.ready = false;
      if (this.pending) return this.pending;
      this.pending = Promise.resolve().then(() => this.run()).then(result => {
        this.error = null;
        return result;
      }, error => {
        this.error = error;
        throw error;
      }).finally(() => { this.pending = null; this.adapter.release?.(); });
      return this.pending;
    }
    async run() {
      let operations = 0;
      let beforeOperation = null;
      while (this.dirty) {
        this.dirty = false;
        await this.adapter.idle();
        const revision = this.revision;
        const cart = this.nextCart || await this.adapter.read();
        if (revision !== this.revision) { this.dirty = true; continue; }
        this.nextCart = null;
        this.version = cartVersion(cart);
        this.adapter.accept?.(cart);
        if (beforeOperation && signature(cart) === signature(beforeOperation)) throw new Error('Warranty cart update did not apply.');
        beforeOperation = null;
        const plan = await this.adapter.plan(cart);
        if (revision !== this.revision || this.adapter.busy?.()) { this.dirty = true; continue; }
        this.adapter.select(plan.targetVariantId, cart, plan);
        if (!plan.changed) {
          await this.adapter.confirm(cart, plan);
          if (revision !== this.revision) { this.dirty = true; continue; }
          this.currentCart = cart;
          this.ready = true;
          this.adapter.ready?.();
          return cart;
        }
        if (++operations > 12) throw new Error('Warranty did not settle.');
        const result = await this.adapter.update(cart, plan);
        if (revision === this.revision) {
          beforeOperation = cart;
          this.nextCart = completeCart(result) ? result : null;
        } else {
          beforeOperation = null;
          this.nextCart = null;
          this.version = null;
        }
        this.dirty = true;
      }
    }
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { WarrantySync, signature };
    return;
  }
  if (root.SissiluxWarranty) { root.SissiluxWarranty.refresh(); return; }
  const document = root.document;
  const selector = 'cart-drawer-upsell[data-siss-warranty-native]';
  const configSelector = '[data-siss-warranty-config]';
  const configNode = document.querySelector(configSelector);
  if (!configNode) return;
  const settings = JSON.parse(configNode.textContent);
  const variantPrices = new Map();
  let configuration;
  let cataloguePromise;
  let marketVariants;
  const warrantyProductIds = [settings.warrantyProductId, ...(settings.legacyWarrantyProductIds || [])].map(String);
  let latestCart;
  let cartKey;
  let optedOut = false;
  let optOutBeforeCart = null;
  let checkingOut = false;
  let replayCheckout = false;
  let regularChangePending = false;
  let pageNeedsRefresh = false;
  let warrantyErrorNode;
  const lockedControls = new Map();
  let quantityActive = false;
  let warrantyWriting = false;
  let quantityVariant;
  let handledQuantity = false;
  let knownItems = document.querySelector('cart-drawer-items');
  const route = suffix => (root.Shopify?.routes?.root || '/') + suffix;

  function widget() {
    const element = document.querySelector(selector);
    if (!element || String(element.dataset.productId) !== String(settings.warrantyProductId)) return null;
    if (!element.warrantyBound) {
      element.warrantyBound = true;
      const nativeVariantData = element.getVariantData?.bind(element);
      element.getVariantData = () => marketVariants || nativeVariantData?.() || [];
      element.updateId = function (id) {
        this.dataset.id = String(id);
        if (this.idInput) this.idInput.value = String(id);
      };
    }
    return element;
  }

  function storageKey(cart) {
    return 'sissilux:warranty:optout:' + String(cart.token || '').split('?')[0];
  }
  function readOptOut(cart) {
    const next = storageKey(cart);
    if (next !== cartKey) {
      cartKey = next;
      try { optedOut = root.localStorage.getItem(cartKey) === '1'; } catch (_) { optedOut = false; }
      if (optOutBeforeCart !== null) setOptOut(optOutBeforeCart);
    }
  }
  function setOptOut(value) {
    optedOut = value;
    if (!cartKey) { optOutBeforeCart = value; return; }
    optOutBeforeCart = null;
    try {
      if (value) root.localStorage.setItem(cartKey, '1');
      else root.localStorage.removeItem(cartKey);
    } catch (_) { /* The current page still respects the customer's choice. */ }
  }

  async function cartRead() {
    // Use the same in-flight snapshot as the other cart consumers.
    if (root.SissiluxDiscounts) {
      const cart = await root.SissiluxDiscounts.refreshCart();
      if (!completeCart(cart)) throw new Error('Cart unavailable.');
      readOptOut(cart); latestCart = cart; return cart;
    }
    const response = await fetch(route('cart.js'), {credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(12000)});
    if (!response.ok) throw new Error('Cart unavailable.');
    const cart = await response.json();
    readOptOut(cart);
    latestCart = cart;
    return cart;
  }
  async function storefront(query, variables) {
    const response = await fetch('/api/2026-07/graphql.json', {
      method:'POST', credentials:'omit', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({query, variables}), signal:AbortSignal.timeout(12000)
    });
    const result = await response.json();
    if (!response.ok || result.errors || !result.data) throw new Error('USD prices unavailable.');
    return result.data;
  }
  function usdCents(price) {
    if (price?.currencyCode !== 'USD' || !/^\d+(?:\.\d{1,2})?$/.test(price.amount)) throw new Error('USD price required.');
    const parts = price.amount.split('.');
    return Number(parts[0]) * 100 + Number((parts[1] || '').padEnd(2, '0'));
  }
  async function catalogue() {
    if (configuration) return configuration;
    if (cataloguePromise) return cataloguePromise;
    cataloguePromise = (async () => {
      // Page the catalogue once per page, independently of cart refreshes.
      const query = `query WarrantyCatalogue($collection: ID!, $warranty: ID!, $after: String, $variantsAfter: String, $country: CountryCode!) @inContext(country: $country) {
        collection(id: $collection) { products(first: 100, after: $after) { nodes { id } pageInfo { hasNextPage endCursor } } }
        product(id: $warranty) { variants(first: 250, after: $variantsAfter) { nodes { id sku availableForSale selectedOptions { value } price { amount currencyCode } } pageInfo { hasNextPage endCursor } } }
      }`;
      const eligible = new Set((settings.eligibleProductIds || []).map(String));
      async function pages(country) {
        let after = null, variantsAfter = null, variantsDone = false, collectionDone = false;
        const variants = new Map();
        do {
          const data = await storefront(query, {collection:'gid://shopify/Collection/'+settings.collectionId, warranty:'gid://shopify/Product/'+settings.warrantyProductId, after, variantsAfter, country});
          if (!data.collection || !data.product) throw new Error('Warranty catalogue unavailable in this market.');
          if (!collectionDone) {
            data.collection.products.nodes.forEach(p => eligible.add(shortId(p.id)));
            collectionDone = !data.collection.products.pageInfo.hasNextPage;
            after = data.collection.products.pageInfo.endCursor;
          }
          if (!variantsDone) {
            data.product.variants.nodes.forEach(v => variants.set(v.id, v));
            variantsDone = !data.product.variants.pageInfo.hasNextPage;
            variantsAfter = data.product.variants.pageInfo.endCursor;
          }
        } while (!collectionDone || !variantsDone);
        return [...variants.values()];
      }
      const country = settings.country || root.Shopify?.country || 'US';
      const usd = await pages('US');
      const local = country === 'US' ? usd : await pages(country);
      marketVariants = local.map(v => ({id:shortId(v.id), sku:v.sku, available:v.availableForSale,
        options:v.selectedOptions.map(o=>o.value), price:Math.round(Number(v.price.amount)*100), compare_at_price:null}));
      configuration = root.SissiluxWarrantyRules.createConfiguration({
        warrantyProductId:settings.warrantyProductId, legacyWarrantyProductIds:settings.legacyWarrantyProductIds,
        warrantyVariants:usd.map(v=>({id:v.id,sku:v.sku,price:(usdCents(v.price)/100).toFixed(2)})),
        eligibleProductIds:[...eligible], tierTable:settings.tierTable,
        minimumUsdSubtotalCents:settings.minimumUsdSubtotalCents, maximumUsdSubtotalCents:settings.maximumUsdSubtotalCents
      });
      return configuration;
    })().finally(() => { cataloguePromise = null; });
    return cataloguePromise;
  }
  async function loadPrices(ids) {
    const missing = [...new Set(ids.map(String))].filter(id => !variantPrices.has(id));
    for (let offset = 0; offset < missing.length; offset += 60) {
      const data = await storefront(`query WarrantyPrices($ids: [ID!]!) @inContext(country: US) {
        nodes(ids: $ids) { ... on ProductVariant { id product { id } price { amount currencyCode } } }
      }`, {ids:missing.slice(offset, offset+60).map(id=>'gid://shopify/ProductVariant/'+id)});
      data.nodes.filter(Boolean).forEach(v => variantPrices.set(shortId(v.id), {cents:usdCents(v.price), productId:shortId(v.product.id)}));
    }
  }
  async function quote(cart) {
    const config = await catalogue();
    const eligible = cart.items.filter(item => config.eligibleProductIds.includes(String(item.product_id)) && String(item.product_id) !== config.warrantyProductId);
    let amount = 0;
    if (cart.currency === 'USD') amount = eligible.reduce((sum,item) => sum + item.original_line_price, 0);
    else {
      await loadPrices(eligible.map(item=>item.variant_id));
      for (const item of eligible) {
        const price = variantPrices.get(String(item.variant_id));
        if (!price) throw new Error('Eligible USD price missing.');
        amount += price.cents * item.quantity;
      }
    }
    return root.SissiluxWarrantyRules.compute(cart, config, false, {eligibleUsdSubtotalCents:amount});
  }
  async function plan(cart) {
    readOptOut(cart);
    latestCart = cart;
    if (optedOut) {
      // Removing coverage needs only confirmed cart lines, not the catalogue
      // or a currency quote. Keep the next purchase quote updating separately.
      quote(cart).then(next => {
        if (optedOut && latestCart === cart && !nativeBusy()) choose(next.targetVariantId, cart, false, next.coverageEligible);
      }).catch(()=>{});
      const updates = {};
      cart.items.filter(item => warrantyProductIds.includes(String(item.product_id))).forEach(item => { updates[item.key] = 0; });
      return {targetVariantId:null, updates, changed:Object.keys(updates).length>0};
    }
    const result = await quote(cart);
    result.displayVariantId = result.targetVariantId;
    return result;
  }

  function choose(id, cart, selected = Boolean(id), coverageEligible) {
    const element = widget();
    if (!element) return;
    const hasEligible = coverageEligible ?? (cart.items.some(item => configuration?.eligibleProductIds.includes(String(item.product_id))));
    element.closest('[data-siss-warranty-block]')?.toggleAttribute('data-no-eligible', !hasEligible);
    const reflectSelection = () => {
      element.dataset.selected = selected ? 'true' : 'false';
      element.toggleBtn?.setAttribute('aria-pressed', String(selected));
    };
    reflectSelection();
    if (!id) return;
    const variant = element.getVariantData?.().find(v => String(v.id) === String(id));
    if (!variant || !variant.available) throw new Error('Warranty variant unavailable in this market.');
    if (element.dataset.id === String(id) && element.idInput?.value === String(id)) return;
    element.variantSelectElements.forEach((select,index) => {
      const value = variant.options[index];
      if (![...select.options].some(option => option.value === value)) {
        const option = document.createElement('option'); option.value = value; option.textContent = value; select.append(option);
      }
      select.value = value;
    });
    element.handleSelectChange({currentTarget:element.variantSelects});
    if (element.dataset.id !== String(id)) throw new Error('Native variant selection failed.');
    reflectSelection();
  }

  function waitFor(test, observeTargets, action) {
    return new Promise((resolve,reject) => {
      let completed = false;
      const observer = new MutationObserver(check);
      const timer = setTimeout(() => finish(new Error('Native cart operation timed out.')), 12000);
      function finish(error) {
        if (completed) return;
        completed = true;
        clearTimeout(timer);
        observer.disconnect();
        document.removeEventListener('cartQuantityUpdated', check);
        error ? reject(error) : resolve();
      }
      function check() { if (test()) finish(); }
      observeTargets.filter(Boolean).forEach(node => observer.observe(node, {subtree:true,childList:true,attributes:true}));
      document.addEventListener('cartQuantityUpdated', check);
      try { action?.(); } catch (error) { finish(error); return; }
      queueMicrotask(check);
    });
  }
  function nativeBusy() {
    return regularChangePending || Boolean(document.querySelector('.cart__items--disabled, product-form button.loading, product-form [aria-disabled="true"].loading, .main-product-atc.loading'));
  }
  async function idle() {
    await Promise.all([customElements.whenDefined('cart-drawer-upsell'),customElements.whenDefined('product-form')]);
    if (nativeBusy()) await waitFor(()=>!nativeBusy(), [document.body]);
  }
  async function refreshPage() {
    if (root.SissiluxCartViews) return root.SissiluxCartViews.refreshPage();
    const items = document.querySelector('cart-items');
    if (!items?.updateCart) return;
    const footer = document.querySelector('#main-cart-footer .js-contents');
    const first = footer?.firstChild;
    if (!footer) return;
    await waitFor(()=>footer.firstChild !== first, [footer], ()=>items.updateCart());
  }
  async function confirm(cart) {
    // compute() has already checked the final warranty against cart.js.
    // Native quantity changes render both carts; do not refresh the drawer
    // again just to compare ordinary-product DOM with the same response.
    if (pageNeedsRefresh) {
      pageNeedsRefresh = false;
      await refreshPage();
    }
  }
  function holdControls() {
    const nodes = [...document.querySelectorAll('cart-drawer-items .quantity__button, cart-drawer-items .quantity__input, cart-drawer-items cart-remove-button button, cart-items .quantity__button, cart-items .quantity__input, cart-items cart-remove-button button, cart-drawer-upsell .upsell-toggle-btn, cart-drawer-upsell select')];
    nodes.forEach(node => {
      if (!lockedControls.has(node)) lockedControls.set(node, node.disabled);
      node.disabled = true;
    });
  }
  function release() {
    if (quantityActive || warrantyWriting) return;
    lockedControls.forEach((disabled, node) => { node.disabled = disabled; });
    lockedControls.clear();
  }
  function quantityFeedback(active) {
    const drawer = document.querySelector('cart-drawer');
    drawer?.toggleAttribute('data-siss-quantity-updating', active);
    drawer?.setAttribute('aria-busy', String(active));
    if (active) holdControls();
    document.querySelectorAll('cart-drawer-items [data-quantity-variant-id]').forEach(input => {
      if (input.dataset.quantityVariantId !== quantityVariant) return;
      input.closest('.cart-item')?.querySelector('.loading-overlay')?.classList.toggle('hidden', !active);
    });
  }
  function beginQuantity(line) {
    if (quantityActive || warrantyWriting) return false;
    quantityActive = true;
    regularChangePending = true;
    handledQuantity = false;
    const input = document.getElementById('Drawer-quantity-'+line);
    quantityVariant = input?.dataset.quantityVariantId;
    sync.ready = false;
    quantityFeedback(true);
    return true;
  }
  function finishQuantity() {
    regularChangePending = false;
    quantityActive = false;
    quantityFeedback(false);
    release();
    if (!sync.pending && !sync.ready) trigger();
  }
  async function warrantyMutation(endpoint, values) {
    holdControls();
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer?.getSectionsToRender?.().map(section => section.id) || ['cart-drawer','cart-icon-bubble'];
    const response = await fetch(route(endpoint), {
      method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({...values, sections, sections_url:root.location.pathname}),
      signal:AbortSignal.timeout(12000)
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || 'Warranty update failed.');
    root.SissiluxDiscounts?.acceptCart(result);
    if (!result.sections?.['cart-drawer']) throw new Error('Warranty cart refresh unavailable.');
    drawer?.renderContents(result, true);
    knownItems = document.querySelector('cart-drawer-items');
    if (quantityActive) quantityFeedback(true);
    pageNeedsRefresh = !root.SissiluxCartViews?.renderPage(result.sections);
    return result;
  }
  async function nativeUpdate(cart, plan) {
    for (const [key, quantity] of Object.entries(plan.updates)) {
      const line = cart.items.find(item => item.key === key);
      const allowed = line ? warrantyProductIds.includes(String(line.product_id)) : configuration.warrantyVariantIds.includes(String(key));
      if (!allowed || ![0,1].includes(quantity)) throw new Error('Warranty-only update required.');
    }
    warrantyWriting = true;
    try { return await warrantyMutation('cart/update.js', {updates:plan.updates}); }
    finally { warrantyWriting = false; release(); }
  }
  function nativeError(error) {
    const element = widget();
    if (!nativeBusy()) {
      element?.toggleBtn?.removeAttribute('disabled');
      element?.variantSelectElements?.forEach(select => select.removeAttribute('disabled'));
    }
    const message = root.cartStrings?.error || 'There was an error while updating your cart. Please try again.';
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors && !errors.textContent.trim()) {
      warrantyErrorNode = document.createElement('span');
      warrantyErrorNode.textContent = message;
      errors.append(warrantyErrorNode);
    }
    root.console.warn('[Warranty]', error.message);
  }
  function checkoutBusy(active) {
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(element => { element.inert = active || !sync.ready; });
    if (!checkingOut && active) return;
    document.querySelectorAll('button[name="checkout"]').forEach(button => {
      button.classList.toggle('loading', active);
      button.setAttribute('aria-busy', String(active));
      button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !active);
    });
  }
  const sync = new WarrantySync({
    idle, busy:nativeBusy, read:cartRead, plan,
    select(id, cart, next) { choose(next.displayVariantId || id, cart, Boolean(id), next.coverageEligible); },
    update:nativeUpdate,
    confirm, release, ready() { warrantyErrorNode?.remove(); warrantyErrorNode = null; checkoutBusy(false); }
  });
  function request(cart, invalidate = false) {
    checkoutBusy(true);
    return sync.request(cart, invalidate).then(result => {
      if (sync.ready) checkoutBusy(false);
      return result;
    }).catch(error => { checkoutBusy(false); nativeError(error); throw error; });
  }
  function trigger(cart, invalidate = false) { request(completeCart(cart) ? cart : undefined, invalidate).catch(()=>{}); }

  function preselect(form) {
    const element = widget();
    const productForm = form.closest('product-form');
    if (!element || !configuration || !latestCart || optedOut || sync.pending || !productForm?.variantInputs || productForm.isCartUpsell) return;
    if (latestCart.items.some(item=>String(item.product_id)===String(settings.warrantyProductId))) return;
    const id = form.querySelector('[name="id"]')?.value;
    const price = variantPrices.get(String(id));
    if (!price || !configuration.eligibleProductIds.includes(price.productId)) return;
    if (productForm.bundleDeals || productForm.quantityBreaks || productForm.mainBundleItems?.length) return;
    const quantity = Number(productForm.quantityPicker?.querySelector('.quantity__input')?.value || form.querySelector('[name="quantity"]')?.value || 1);
    const projected = structuredClone(latestCart);
    projected.items.push({id,variant_id:id,product_id:price.productId,quantity,key:'prospective:'+id,original_line_price:price.cents*quantity});
    let amount = 0;
    for (const item of projected.items) {
      if (!configuration.eligibleProductIds.includes(String(item.product_id))) continue;
      if (latestCart.currency==='USD') amount += item.original_line_price;
      else {
        const p = variantPrices.get(String(item.variant_id));
        if (!p) return;
        amount += p.cents*item.quantity;
      }
    }
    const next = root.SissiluxWarrantyRules.compute(projected,configuration,false,{eligibleUsdSubtotalCents:amount});
    choose(next.targetVariantId, projected, Boolean(next.targetVariantId), next.coverageEligible);
  }
  function warmPrices() {
    const ids = [...document.querySelectorAll('product-form [name="id"]')].map(input=>input.value).filter(id=>/^\d+$/.test(id));
    loadPrices(ids).catch(()=>{});
  }

  document.addEventListener('click', event => {
    const toggle = event.target.closest?.(selector+' .upsell-toggle-btn');
    if (toggle) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const element = widget();
      setOptOut(element?.dataset.selected==='true');
      if (element) element.dataset.selected = optedOut ? 'false' : 'true';
      trigger(undefined, true);
      return;
    }
    const remove = event.target.closest?.('cart-remove-button');
    if (remove?.closest('.cart-item')?.classList.contains('cart-item--product-'+settings.warrantyHandle)) setOptOut(true);
    const checkout = event.target.closest?.('button[name="checkout"], a[href="/checkout"]');
    if (!checkout || replayCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutAfterSync(checkout);
  }, true);
  async function checkoutAfterSync(button) {
    if (checkingOut) return;
    checkingOut = true;
    checkoutBusy(true);
    try {
      // A confirmed unchanged cart needs no extra read merely to checkout.
      if (sync.pending) await sync.pending;
      if (nativeBusy()) await idle();
      const cart = root.SissiluxDiscounts?.cart;
      if (!sync.ready || (completeCart(cart) && cartVersion(cart) !== sync.version)) await request(cart);
      if (!sync.ready) throw new Error('Warranty not ready.');
      const current = button.id ? document.getElementById(button.id) : button;
      if (!current) throw new Error('Checkout button changed.');
      replayCheckout = true;
      current.click();
    } catch (_) { checkoutBusy(false); }
    finally { replayCheckout = false; checkingOut = false; }
  }
  document.addEventListener('submit', event => {
    if (replayCheckout) return;
    if (event.submitter?.name==='checkout') {
      event.preventDefault();
      event.stopImmediatePropagation();
      checkoutAfterSync(event.submitter);
    } else if (event.target.closest?.('product-form') && !event.target.closest(selector)) {
      preselect(event.target);
      sync.ready = false;
    }
  }, true);
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches?.('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')) {
      regularChangePending = true;
      handledQuantity = false;
      sync.ready = false;
      if (Number(input.value)===0 && input.closest('.cart-item')?.classList.contains('cart-item--product-'+settings.warrantyHandle)) setOptOut(true);
    }
    if (input.closest?.('product-form, variant-selects, product-info')) warmPrices();
  }, true);
  document.addEventListener('cartQuantityUpdated', () => {
    regularChangePending = false;
    if (!handledQuantity) trigger();
  });
  // A shared read can finish after the native cartUpdate event. Consume that
  // confirmed snapshot, rather than leaving warranty on a pre-add cart.
  document.addEventListener('sissilux:discounts-updated', () => {
    const cart = root.SissiluxDiscounts?.cart;
    if (!warrantyWriting && completeCart(cart)) trigger(cart);
  });
  if (typeof subscribe==='function' && typeof PUB_SUB_EVENTS==='object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event?.source === 'cart-items') { regularChangePending = false; handledQuantity = true; }
      else pageNeedsRefresh = !root.SissiluxCartViews?.renderPage(event?.cartData?.sections);
      knownItems = document.querySelector('cart-drawer-items');
      const cart = root.SissiluxDiscounts?.cartFromResponse?.(event?.cartData) || event?.cartData;
      if (completeCart(cart)) trigger(cart);
      else if (root.SissiluxDiscounts) {
        // The shared cartUpdate listener invalidates pre-mutation reads.
        // Defer until all synchronous listeners have seen that event.
        sync.ready = false;
        queueMicrotask(() => trigger(undefined, true));
      } else trigger(undefined, true);
    });
    subscribe(PUB_SUB_EVENTS.variantChange, warmPrices);
  }
  const observer = new MutationObserver(records => {
    widget();
    const contentChanged = records.some(record => record.type==='childList' && [...record.addedNodes,...record.removedNodes].some(node => node.nodeType===1 && (node.matches?.('cart-drawer-items, .drawer__inner, .cart-item, .cart-drawer__body') || node.querySelector?.('cart-drawer-items, .cart-item'))));
    const currentItems = document.querySelector('cart-drawer-items');
    if (quantityActive) quantityFeedback(true);
    if (contentChanged && currentItems !== knownItems) {
      knownItems = currentItems;
      // The pending operation owns the cart state. DOM replacement is not a
      // newer cart event and must never enqueue the previously cached cart.
      if (sync.pending || !sync.ready) return;
      const cart = root.SissiluxDiscounts?.cart;
      // Rebind a settled quote to the replacement card without another GET.
      trigger(completeCart(cart) ? cart : undefined, true);
    }
  });
  observer.observe(document.querySelector('cart-drawer') || document.body, {childList:true,subtree:true});
  root.addEventListener('storage', event => { if (event.key===cartKey) { optedOut=event.newValue==='1'; trigger(undefined, true); } });
  root.addEventListener('pageshow', trigger);
  root.addEventListener('focus', trigger);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) trigger(); });
  root.SissiluxWarranty = {refresh:trigger, ensure:request, beginQuantity, finishQuantity, get busy(){return quantityActive || warrantyWriting;}, get ready(){return sync.ready;}, get error(){return sync.error;}};
  catalogue().then(warmPrices).catch(()=>{});
  trigger();
})(typeof globalThis==='object' ? globalThis : this);
